import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Mic, Square, Circle } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";

const readinessPdf =
  "https://res.cloudinary.com/dcfotquxt/image/upload/v1763613933/Ai-AssEss/GoWest_Employee_AI_Readiness_Interview_Questions_mgiqvv.pdf";

const policiesPdf =
  "https://res.cloudinary.com/dcfotquxt/image/upload/v1764874006/Ai-AssEss/AI_Policies_Guidelines_Builder_Packet.docx_v0sxxb.pdf";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function RecordingDialog({
  open,
  onOpenChange,
  intervieweeName,
  intervieweeType,
  onStartRecording,
  onStopRecording,
  isRecording = false,
  elapsedSeconds = 0,
}) {
  const [selectedType, setSelectedType] = useState(intervieweeType);

  // Reset selected type when dialog opens with new interviewee
  useEffect(() => {
    if (open) setSelectedType(intervieweeType);
  }, [open, intervieweeType]);

  const pdfUrl = selectedType === "Policies" ? policiesPdf : readinessPdf;
  const questionnaireTitle =
    selectedType === "Policies"
      ? "AI Policies & Guidelines Questionnaire"
      : "AI Readiness Assessment Questionnaire";

  const handleClose = (val) => {
    if (!val && isRecording && onStopRecording) {
      onStopRecording();
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="font-display flex items-center gap-3">
            Record &amp; Transcript — {intervieweeName}
            <Badge variant="secondary" className="font-normal">
              {intervieweeType}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Left: Recording Controls */}
          <div className="w-72 shrink-0 border-r flex flex-col items-center justify-center gap-6 p-6">
            {/* Status indicator */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {isRecording && (
                <>
                  <Circle className="h-3 w-3 fill-red-500 text-red-500 animate-pulse" />
                  <span className="text-red-400 font-medium">Recording</span>
                </>
              )}
              {!isRecording && <span>Ready to record</span>}
            </div>

            {/* Timer */}
            <div className="text-4xl font-mono font-bold tracking-wider">
              {formatTime(elapsedSeconds || 0)}
            </div>

            {/* Mic button */}
            <button
              onClick={isRecording ? onStopRecording : onStartRecording}
              className={`h-20 w-20 rounded-full flex items-center justify-center transition-all ${
                isRecording
                  ? "bg-red-500/20 border-2 border-red-500 text-red-400 animate-pulse"
                  : "bg-primary/20 border-2 border-primary text-primary hover:bg-primary/30"
              }`}
            >
              {isRecording ? (
                <Square className="h-8 w-8" />
              ) : (
                <Mic className="h-8 w-8" />
              )}
            </button>

            {/* Action buttons */}
            <div className="flex gap-3">
              {!isRecording ? (
                <Button onClick={onStartRecording} className="gap-1.5">
                  <Mic className="h-4 w-4" />
                  Start Recording
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={onStopRecording}
                  className="gap-1.5"
                >
                  <Square className="h-4 w-4" />
                  Stop &amp; Save
                </Button>
              )}
            </div>

            <p className="text-[11px] text-muted-foreground text-center mt-2">
              Audio will be transcribed automatically after recording is
              stopped.
            </p>
          </div>

          {/* Right: Questionnaire Preview */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="px-5 py-3 border-b bg-muted/30 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-sm font-display">
                  {questionnaireTitle}
                </h3>
                <p className="text-[11px] text-muted-foreground">
                  Use these questions as a guide during the interview
                </p>
              </div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="text-xs rounded-md border bg-background px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="Readiness">AI Readiness Assessment</option>
                <option value="Policies">AI Policies &amp; Guidelines</option>
              </select>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-5 h-full">
                <div className="rounded-lg border overflow-hidden h-[60vh]">
                  <iframe
                    title={questionnaireTitle}
                    src={pdfUrl}
                    className="w-full h-full"
                  />
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
